

import axios from 'axios';
import { getAccessToken } from '../auth/tokenStore';

class FrontendLogger {
    constructor() {
        this.logQueue = [];
        this.isProcessing = false;
        this.batchSize = 10;
        // Each flush is a fully-authenticated POST that competes with the UI's
        // own requests. Flushing every 2s cost more than the logs were worth.
        this.flushInterval = 15000;
        this.setupAutoFlush();
        
        // Configure base URL based on environment
        const isProduction = process.env.NODE_ENV === 'production';
        const devHost =
            typeof window !== 'undefined' && window.location.hostname
                ? window.location.hostname
                : 'localhost';
        this.baseURL = isProduction
            ? process.env.REACT_APP_API_URL || 'https://backend.test-PearlCrm.com'
            : `http://${devHost}:8000`;
            
        console.log('Logger initialized with baseURL:', this.baseURL);
    }

    setupAutoFlush() {
        // Auto-flush logs periodically
        setInterval(() => {
            if (this.logQueue.length > 0) {
                this.processQueue();
            }
        }, this.flushInterval);

        // Add event listener for page unload
        window.addEventListener('beforeunload', () => {
            this.forceFlush();
        });
    }

    async forceFlush() {
        if (this.logQueue.length > 0) {
            try {
                const token = getAccessToken();
                const log_response = await axios.post(
                    `${this.baseURL}/log/frontend/batch`,
                    { logs: this.logQueue },
                    {
                        headers: token ? { Authorization: `Bearer ${token}` } : {}
                    }
                );
                console.log('Logs flushed successfully:', log_response.data);
                this.logQueue = []; // Clear queue after successful flush
            } catch (error) {
                console.error('Failed to force flush logs:', error);
            }
        }
    }

    async log(action, details) {
        const logEntry = {
            timestamp: new Date().toISOString(),
            action,
            details,
            userAgent: navigator.userAgent,
            url: window.location.href,
            userId: localStorage.getItem('userId') || 'anonymous',
            username: localStorage.getItem('username') || 'anonymous'
        };

        this.logQueue.push(logEntry);

        // Process queue if it reaches batch size
        if (this.logQueue.length >= this.batchSize) {
            await this.processQueue();
        }
    }

    async processQueue() {
        if (this.isProcessing || this.logQueue.length === 0) return;

        this.isProcessing = true;
        try {
            const batch = this.logQueue.splice(0, this.batchSize);
            const token = getAccessToken();

            await axios.post(
                `${this.baseURL}/log/frontend/batch`,
                { logs: batch },
                {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                }
            );
        } catch (error) {
            console.error('Failed to send logs to backend:', error);
            // Put failed logs back in queue
            this.logQueue.unshift(...this.logQueue.splice(0, this.batchSize));
        } finally {
            this.isProcessing = false;
            if (this.logQueue.length > 0) {
                setTimeout(() => this.processQueue(), 1000);
            }
        }
    }

    // CRUD operation logging
    async logCreate(entity, data) {
        await this.log('CREATE', { entity, data });
    }

    async logRead(entity, id) {
        await this.log('READ', { entity, id });
    }

    async logUpdate(entity, id, data) {
        await this.log('UPDATE', { entity, id, data });
    }

    async logDelete(entity, id) {
        await this.log('DELETE', { entity, id });
    }

    // API call logging
    async logApiCall(method, url, requestData, responseData, status) {
        await this.log('API_CALL', {
            method,
            url,
            requestData,
            responseData,
            status
        });
    }

    // Error logging
    async logError(error, context) {
        await this.log('ERROR', {
            error: error.message,
            stack: error.stack,
            context
        });
    }
}

export const frontendLogger = new FrontendLogger(); 